import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '@/services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('insurelab_token'));
  const [loading, setLoading] = useState(true);
  const [requires2FA, setRequires2FA] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState(null);

  const checkAuth = useCallback(async () => {
    if (token) {
      try {
        const userData = await api.getMe();
        setUser(userData);
      } catch {
        localStorage.removeItem('insurelab_token');
        setToken(null);
        setUser(null);
      }
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password, totpCode = null) => {
    const response = await api.login(email, password, totpCode);
    
    if (response.requires_2fa) {
      setRequires2FA(true);
      setPendingCredentials({ email, password });
      return { requires2FA: true };
    }
    
    localStorage.setItem('insurelab_token', response.access_token);
    setToken(response.access_token);
    setUser(response.user);
    setRequires2FA(false);
    setPendingCredentials(null);
    return { success: true, user: response.user };
  };

  const verify2FA = async (totpCode) => {
    if (!pendingCredentials) throw new Error('No pending credentials');
    return login(pendingCredentials.email, pendingCredentials.password, totpCode);
  };

  const register = async (userData) => {
    const response = await api.register(userData);
    localStorage.setItem('insurelab_token', response.access_token);
    setToken(response.access_token);
    setUser(response.user);
    return response.user;
  };

  const loginWithGoogle = async (credential) => {
    const response = await api.googleOAuth(credential);
    localStorage.setItem('insurelab_token', response.access_token);
    setToken(response.access_token);
    setUser(response.user);
    return response.user;
  };

  const logout = () => {
    localStorage.removeItem('insurelab_token');
    setToken(null);
    setUser(null);
  };

  const enable2FA = async () => api.enable2FA();
  const verify2FASetup = async (code) => {
    const result = await api.verify2FA(code);
    setUser(prev => ({ ...prev, two_factor_enabled: true }));
    return result;
  };
  const disable2FA = async (code) => {
    const result = await api.disable2FA(code);
    setUser(prev => ({ ...prev, two_factor_enabled: false }));
    return result;
  };

  const hasPermission = (permission) => {
    const role = user?.role || 'user';
    const permissions = {
      master_admin: ['*'],
      carrier_admin: ['manage_products', 'view_analytics', 'manage_mgas', 'manage_brokers'],
      mga_admin: ['manage_products', 'view_analytics', 'manage_brokers', 'bind_policies'],
      broker: ['create_quotes', 'view_clients', 'manage_policies', 'view_analytics'],
      user: ['create_policy', 'view_own_policies', 'chat']
    };
    const userPerms = permissions[role] || permissions.user;
    return userPerms.includes('*') || userPerms.includes(permission);
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, requires2FA,
      login, verify2FA, register, loginWithGoogle, logout,
      enable2FA, verify2FASetup, disable2FA,
      hasPermission,
      isAuthenticated: !!user,
      isAdmin: ['master_admin', 'carrier_admin', 'mga_admin'].includes(user?.role)
    }}>
      {children}
    </AuthContext.Provider>
  );
};
